import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar";
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronRight, ExternalLink, Lock } from 'lucide-react';
import { ReactNode, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { usePermissions } from '@/auth/PermissionsContext';

export interface NavSubItem {
  title: string;
  url: string;
  icon?: ReactNode;
  disabled?: boolean;
  requiredPermission?: string;
  requiredPermissions?: string[];
  // Subitem de ação: abre destino externo em vez de navegar por rota interna.
  onClick?: () => void;
}

export interface NavItem {
  title: string;
  url: string;
  icon?: ReactNode;
  isActive?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  primaryOnly?: boolean;
  subItems?: NavSubItem[];
  requiredPermission?: string;
  requiredPermissions?: string[];
  // Item de ação: abre destino externo em vez de navegar por rota interna.
  onClick?: () => void;
}

export function NavMain({ items }: { items: NavItem[] }) {
  const location = useLocation();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const { hasPermission, hasAnyPermission, isPrimary } = usePermissions();

  // Filtrar items com base em permissões — primary owner vê tudo
  const filteredItems = useMemo(() => {
    return items.filter((item) => !item.hidden).map((item) => {
      const hasAccess = (() => {
        if (isPrimary) return true;
        if (item.requiredPermissions?.length) return hasAnyPermission(item.requiredPermissions);
        if (item.requiredPermission) return hasPermission(item.requiredPermission);
        return true;
      })();
      if (!hasAccess) return { ...item, disabled: true };

      // Filtar sub-items com base na permissão
      if (item.subItems?.length) {
        const visibleSubItems = item.subItems.filter((sub) => {
          if (isPrimary) return true;
          if (sub.requiredPermissions?.length) return hasAnyPermission(sub.requiredPermissions);
          if (sub.requiredPermission) return hasPermission(sub.requiredPermission);
          return true;
        });
        return { ...item, subItems: visibleSubItems };
      }

      return item;
    });
  }, [items, hasPermission, hasAnyPermission, isPrimary]);

  const activeRoute = (route: string, exact: boolean = false) => {
    // Item de ação não tem rota; sem esta guarda o includes('') marca tudo como ativo.
    if (!route) {
      return false;
    }

    if (exact) {
      return location?.pathname === route;
    }

    if (location?.pathname === route) {
      return true;
    }

    if (location?.pathname.includes(route)) {
      return true;
    }

    return false;
  };

  useEffect(() => {
    const itemsToExpand: string[] = [];
    const currentPath = location.pathname;

    filteredItems.forEach((item) => {
      if (item.subItems && item.subItems.length > 0) {
        const hasActiveSubItem = item.subItems.some((subItem) => {
          if (subItem.onClick || !subItem.url) return false;
          if (currentPath === subItem.url) return true;
          if (currentPath.includes(subItem.url)) return true;
          return false;
        });

        if (hasActiveSubItem) {
          itemsToExpand.push(item.title);
        }
      }
    });

    if (itemsToExpand.length > 0) {
      setExpandedItems((prev) => {
        const combined = [...prev, ...itemsToExpand];
        const uniqueItems = Array.from(new Set(combined));
        return uniqueItems;
      });
    }
  }, [location.pathname, filteredItems]);

  const toggleExpand = (title: string) => {
    setExpandedItems((prev) =>
      prev.includes(title)
        ? prev.filter((item) => item !== title)
        : [...prev, title]
    );
  };

  const isExpanded = (title: string) => expandedItems.includes(title);

  return (
    <SidebarGroup>
      <SidebarMenu className='gap-3'>
        {filteredItems.map((item) => (
          <div key={item.title}>
            {!item.disabled ? (
              <SidebarMenuItem>
                {item.subItems && item.subItems.length > 0 ? (
                  <>
                    <div className='flex items-center'>
                      <Link to={item.url} className='flex-1'>
                        <SidebarMenuButton
                          isActive={activeRoute(item?.url)}
                          tooltip={item.title}
                        >
                          {item.icon && item.icon}
                          <span>{item.title}</span>
                        </SidebarMenuButton>
                      </Link>
                      <motion.button
                        onClick={() => toggleExpand(item.title)}
                        className='p-2 hover:bg-sidebar-accent rounded-md transition-colors'
                        whileTap={{ scale: 0.95 }}
                      >
                        <motion.div
                          animate={{ rotate: isExpanded(item.title) ? 90 : 0 }}
                          transition={{ duration: 0.2, ease: 'easeInOut' }}
                        >
                          <ChevronRight size={16} className='text-muted-foreground' />
                        </motion.div>
                      </motion.button>
                    </div>
                    <AnimatePresence initial={false}>
                      {isExpanded(item.title) && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: 'easeInOut' }}
                          style={{ overflow: 'hidden' }}
                        >
                          <SidebarMenuSub className='mt-2 ml-2 border-l border-sidebar-border pl-2'>
                            {item.subItems.map((subItem) => (
                              <SidebarMenuSubItem key={subItem.title}>
                                {!subItem.disabled ? (
                                  subItem.onClick ? (
                                    <SidebarMenuSubButton
                                      asChild
                                      className='flex w-full cursor-pointer items-center gap-3 py-2 px-2 hover:bg-sidebar-accent/50 rounded-md transition-colors'
                                    >
                                      <button type="button" onClick={subItem.onClick}>
                                        {subItem.icon && (
                                          <span className='flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-md bg-sidebar-accent/30 p-0.5'>
                                            <span className=''>
                                              {subItem.icon}
                                            </span>
                                          </span>
                                        )}
                                        <span className='text-sm font-medium truncate'>{subItem.title}</span>
                                        <ExternalLink size={12} className='ml-auto text-muted-foreground' />
                                      </button>
                                    </SidebarMenuSubButton>
                                  ) : (
                                    <Link to={subItem.url}>
                                      <SidebarMenuSubButton
                                        isActive={activeRoute(subItem.url, true)}
                                        className='flex items-center gap-3 py-2 px-2 hover:bg-sidebar-accent/50 rounded-md transition-colors'
                                      >
                                        {subItem.icon && (
                                          <span className='flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-md bg-sidebar-accent/30 p-0.5'>
                                            <span className=''>
                                              {subItem.icon}
                                            </span>
                                          </span>
                                        )}
                                        <span className='text-sm font-medium truncate'>{subItem.title}</span>
                                      </SidebarMenuSubButton>
                                    </Link>
                                  )
                                ) : (
                                  <div
                                    className='flex items-center gap-3 py-2 px-2 opacity-50 cursor-not-allowed rounded-md'
                                    aria-disabled="true"
                                  >
                                    {subItem.icon && (
                                      <span className='flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-md bg-sidebar-accent/20 p-0.5'>
                                        <span className='[&>svg]:w-4 [&>svg]:h-4'>
                                          {subItem.icon}
                                        </span>
                                      </span>
                                    )}
                                    <span className='text-sm truncate text-sidebar-foreground'>{subItem.title}</span>
                                    <Lock size={12} className='ml-auto text-muted-foreground' />
                                  </div>
                                )}
                              </SidebarMenuSubItem>
                            ))}
                          </SidebarMenuSub>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </>
                ) : item.onClick ? (
                  <SidebarMenuButton
                    onClick={item.onClick}
                    tooltip={item.title}
                  >
                    {item.icon && item.icon}
                    <span>{item.title}</span>
                    <ExternalLink size={12} className='ml-auto text-muted-foreground' />
                  </SidebarMenuButton>
                ) : (
                  <Link to={item.url}>
                    <SidebarMenuButton
                      isActive={activeRoute(item?.url)}
                      tooltip={item.title}
                    >
                      {item.icon && item.icon}
                      <span>{item.title}</span>
                    </SidebarMenuButton>
                  </Link>
                )}
              </SidebarMenuItem>
            ) : (
              <SidebarMenuItem>
                <SidebarMenuButton
                  disabled
                  isActive={activeRoute(item?.url)}
                  tooltip={item.title}
                  className='flex justify-between items-center'
                >
                  <div className='flex items-center gap-2'>
                    {item.icon && item.icon}
                    <span>{item.title}</span>
                  </div>
                  <Lock size={14} style={{ marginLeft: 4 }} />
                </SidebarMenuButton>
              </SidebarMenuItem>
            )}
          </div>
        ))}
      </SidebarMenu>
    </SidebarGroup>
  );
}
